import { useEffect, useState } from "react";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { Chip, FormControl, InputLabel, OutlinedInput } from "@mui/material";
import {
  exportAccountHolders,
  exportTenants,
  loadTenants,
  saveTenant,
  totalTenants,
} from "../services/tenants.service";
import { Apartment, Contacts, Edit, Person, Visibility } from "@mui/icons-material";
import { useSelector } from "react-redux";
import AddNewOrganization from "../components/modals/addNewOrganization";
import AddNewUser from "../components/modals/addNewUser";
import { issueResponseMessage, issueWarnMessage } from "../actions/message";
import {
  isEmptyObject,
  isEmptyString,
  isNullUndefined,
  isValidPhoneNumber,
} from "../util/util";
import {
  MainContainer,
  TopBarContainedBrandButton,
  TopBarContainer,
} from "../constants/component.constants";
import { ResponsiveRow } from "../constants/layout.constants";
import Permit from "../util/permit";
import MuiDataGrid from "../components/modules/MuiDataGrid";
import ViewTenantModal from "../components/modals/rentals/leases/viewTenantModal";
import EditUserModal from "../components/modules/users/editUserModal";
import { getIdTypes } from "../services/list.service";
import { getUserRoles, saveUser } from "../services/users.service";
import { deepOrange, green } from "@mui/material/colors";

export default function Tenants() {
  const user = useSelector((state) => state.auth.user);
  const [tenantName, setTenantName] = useState("");
  const [selectedTenant, setSelectedTenant] = useState("");
  const [tenantList, setTenantList] = useState([]);
  const [total, setTotalTenants] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 50,
    page: 0,
  });
  const [residentialTenant, setResidentialTenant] = useState(true);
  const [hoaTenant, setHoaTenant] = useState(false);
  const [task, setTask] = useState("");
  const [addOrganizationModal, setAddOrganizationModal] = useState(false);
  const [addUserModal, setAddUserModal] = useState(false);
  const [viewTenantModal, setViewTenantModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editCompanyContactModal, setEditCompanyContactModal] = useState(false);
  const [selectedCompanyContact, setSelectedCompanyContact] = useState();
  const [idTypes, setIdTypes] = useState([]);
  const [userRoles, setUserRoles] = useState([]);

  const fetchTenants = async (controller = null) => {
    setLoading(true);
    totalTenants(tenantName, { signal: controller?.signal }).then((data) => {
      setTotalTenants(data.total);
    });
    loadTenants(tenantName, paginationModel.page, paginationModel.pageSize, {
      signal: controller?.signal,
    })
      .then((data) => {
        setTenantList(data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      fetchTenants(controller);
    }, 500);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    tenantName,
    paginationModel.page,
    paginationModel.pageSize,
    user.proxyFacility,
  ]);

  useEffect(() => {
    if (
      !isNullUndefined(user) &&
      !isNullUndefined(user.services) &&
      user.services.includes("METER") &&
      !user.services.includes("PREMIUM") &&
      !user.services.includes("CORE") &&
      !user.services.includes("LEAN")
    ) {
      setResidentialTenant(false);
      setHoaTenant(false);
    } else if (
      !isNullUndefined(user) &&
      !isNullUndefined(user.services) &&
      user.services.includes("HOA")
    ) {
      setResidentialTenant(false);
      setHoaTenant(true);
    } else {
      setResidentialTenant(true);
      setHoaTenant(false);
    }
  }, [user]);

  useEffect(() => {
    getIdTypes().then((idTypes) => setIdTypes(idTypes));
    getUserRoles().then((userRoles) => setUserRoles(userRoles));
  }, []);

  function editTenantClicked(item) {
    setSelectedTenant(item);
    setTask("Edit");
    if (item.type === "Company") {
      setAddOrganizationModal(true);
    }
    if (item.type === "Individual") {
      setAddUserModal(true);
    }
  }

  function handleAddOrgClose() {
    setAddOrganizationModal(false);
    setTask("");
    fetchTenants();
  }

  function handleAddUserClose() {
    setAddUserModal(false);
    setTask("");
    fetchTenants();
  }

  function validateForm() {}

  function handleViewClick(item) {
    setSelectedTenant(item);
    setViewTenantModal(true);
  }

  function handleViewTenantClose() {
    setViewTenantModal(false);
    setSelectedTenant(null);
  }

  function handleEditCompanyContactClicked(item) {
    setEditCompanyContactModal(true);
    setSelectedCompanyContact(item.contact);
    setTask("Edit");
  }

  function validateUserForm(sendData) {
    if (isNullUndefined(sendData)) {
      issueWarnMessage(
        "Could not find user data, please refresh the page and try again.",
      );
      return false;
    }
    if (isEmptyString(sendData.firstName) || isEmptyString(sendData.lastName)) {
      issueWarnMessage("First name and Last name are required.");
      return false;
    }
    if (
      isNullUndefined(sendData.mobilePhone) ||
      isEmptyString(sendData.mobilePhone)
    ) {
      issueWarnMessage("Mobile Phone number is required.");
      return false;
    }
    const { isValid, errors } = isValidPhoneNumber(sendData?.mobilePhone);
    if (!isValid) {
      issueWarnMessage(`Mobile phone number is invalid. ${errors.join(". ")}`);
      return false;
    }

    if (sendData?.officePhone) {
      if (sendData?.officePhone === sendData?.mobilePhone) {
        issueWarnMessage(
          `Office phone number cannot be same as mobile phone number. Leave blank if you don't have a separate phone number.`,
        );
        return false;
      }
      const { isValid, errors } = isValidPhoneNumber(sendData?.officePhone);
      if (!isValid) {
        issueWarnMessage(
          `Office phone number is invalid. ${errors.join(". ")}`,
        );
        return false;
      }
    }
    if (
      isNullUndefined(sendData.idNumber) ||
      isEmptyString(sendData.idNumber) ||
      isNullUndefined(sendData.idTypeCd) ||
      isEmptyString(sendData.idTypeCd)
    ) {
      issueWarnMessage("Must provide and ID number and ID type.");
      return false;
    }

    return true;
  }

  function handleSaveCompanyContactClicked() {
    let params = selectedCompanyContact;
    if (validateUserForm(params)) {
      saveUser(params).then((response) => {
        if (response) {
          issueResponseMessage(response);
          if (response.type !== "error") {
            fetchTenants();
            setEditCompanyContactModal(false);
            setSelectedCompanyContact();
          }
        }
      });
    }
  }

  function handleUpdateTenant(data) {
    if (validateForm) {
      if (residentialTenant) {
        data.id = selectedTenant.tenantId;
      }
      saveTenant(data).then((response) => {
        if (response) {
          issueResponseMessage(response);
          if (response.type !== "error") {
            handleAddUserClose();
            loadTenants(
              tenantName,
              paginationModel.page,
              paginationModel.pageSize,
            ).then((data) => {
              setTenantList(data);
            });
            totalTenants(tenantName).then((data) => setTotalTenants(data.total));
          }
        }
      });
    }
  }

    function getChipProps(params) {
    if (!isNullUndefined(params.value)) {
      if (params.value === "Company") {
        return {
          icon: <Apartment style={{ fill: deepOrange[500] }} />,
          label: "Company",
          style: {
            borderColor: deepOrange[500],
          },
        };
      } else if (params.value === "Individual") {
        return {
          icon: <Person style={{ fill: green[500] }} />,
          label: "Individual",
          style: {
            borderColor: green[500],
          },
        };
      } else {
        return "N/A";
      }
    }
  }

  const hideOwnerEdit =
    user.proxyRole === "OWN" &&
    (user.services?.includes("PREMIUM") || user.services?.includes("CORE"));

  const columns1 = [
    { headerName: "Tenant Name", field: "name", width: 200 },
    {
      field: "type",
      headerName: "Type",
      minWidth: 150,
      flex: 2,
      renderCell: (params) => {
        return (
          <Chip variant="outlined" size="small" {...getChipProps(params)} />
        );
      },
    },
    { headerName: "Property", field: "property", minWidth: 200, flex: 1 },
    { headerName: "Units Leased", field: "units", minWidth: 250, flex: 1.25 },
    {
      headerName: "Lease Start",
      field: "leaseStart",
      minWidth: 150,
      flex: 0.75,
    },
    { headerName: "Lease End", field: "leaseEnd", minWidth: 150, flex: 0.75 },
    { headerName: "Phone", field: "mobilePhone", minWidth: 150, flex: 0.75 },
    { headerName: "Email", field: "email", minWidth: 175, flex: 0.875 },
    {
      headerName: "Actions",
      field: "actions",
      width: 100,
      type: "actions",
      getActions: (params) => {
        let arr = [];
        if (!hideOwnerEdit) {
          arr.push(
            <GridActionsCellItem
              icon={<Edit />}
              onClick={() => editTenantClicked(params.row)}
              label="Edit"
              showInMenu
              disabled={user.proxyRole === "REC"}
            />,
          );
        }

        arr.push(
          <GridActionsCellItem
            icon={<Visibility />}
            onClick={() => handleViewClick(params.row)}
            label="View"
            showInMenu
            disabled={user.proxyRole === "REC"}
          />,
        );

        if (
          !hideOwnerEdit &&
          params.row.type === "Company" &&
          !isEmptyObject(params.row.contact)
        ) {
          arr.push(
            <GridActionsCellItem
              icon={<Contacts />}
              onClick={() => handleEditCompanyContactClicked(params.row)}
              label="Edit Company Contact"
              showInMenu
            />,
          );
        }

        return arr;
      },
    },
  ];

  const columns2 = [
    { headerName: "Holder Name", field: "name", minWidth: 250, flex: 1.25 },
    {
      field: "type",
      headerName: "Type",
      minWidth: 150,
      flex: 2,
      renderCell: (params) => {
        return (
          <Chip variant="outlined" size="small" {...getChipProps(params)} />
        );
      },
    },
    { headerName: "Property", field: "property", minWidth: 200, flex: 1 },
    { headerName: "Meters Billed", field: "meters", minWidth: 250, flex: 1.25 },
    {
      headerName: "Billing Start",
      field: "billingStart",
      minWidth: 150,
      flex: 0.25,
    },
    {
      headerName: "Billing End",
      field: "billingEnd",
      minWidth: 150,
      flex: 0.25,
    },
    { headerName: "Phone", field: "mobilePhone", minWidth: 150, flex: 0.25 },
    { headerName: "Email", field: "email", minWidth: 175, flex: 0.875 },
    {
      headerName: "Actions",
      field: "actions",
      width: 100,
      type: "actions",
      getActions: (params) => {
        let arr = [];
        if (!hideOwnerEdit) {
          arr.push(
            <GridActionsCellItem
              icon={<Edit />}
              onClick={() => editTenantClicked(params.row)}
              label="Edit"
              showInMenu
              disabled={user.proxyRole === "REC"}
            />,
          );
        }

        arr.push(
          <GridActionsCellItem
            icon={<Visibility />}
            onClick={() => handleViewClick(params.row)}
            label="View"
            showInMenu
            disabled={user.proxyRole === "REC"}
          />,
        );

        if (
          !hideOwnerEdit &&
          params.row.type === "Company" &&
          !isEmptyObject(params.row.contact)
        ) {
          arr.push(
            <GridActionsCellItem
              icon={<Contacts />}
              onClick={() => handleEditCompanyContactClicked(params.row)}
              label="Edit Company Contact"
              showInMenu
            />,
          );
        }

        return arr;
      },
    },
  ];

  const columns3 = [
    { headerName: "Tenant Name", field: "name", minWidth: 250, flex: 1.75 },
    {
      field: "type",
      headerName: "Type",
      minWidth: 150,
      flex: 2,
      renderCell: (params) => {
        return (
          <Chip variant="outlined" size="small" {...getChipProps(params)} />
        );
      },
    },
    { headerName: "Property", field: "property", minWidth: 200, flex: 1 },
    { headerName: "Unit", field: "units", minWidth: 250, flex: 1.25 },
    {
      headerName: "Ownership Start",
      field: "ownershipStart",
      minWidth: 150,
      flex: 0.75,
    },
    {
      headerName: "Ownership End",
      field: "ownershipEnd",
      minWidth: 150,
      flex: 0.75,
    },
    { headerName: "Phone", field: "mobilePhone", minWidth: 150, flex: 0.75 },
    { headerName: "Email", field: "email", minWidth: 175, flex: 0.875 },
    {
      headerName: "Actions",
      field: "actions",
      width: 100,
      type: "actions",
      getActions: (params) => {
        let arr = [];
        if (!hideOwnerEdit) {
          arr.push(
            <GridActionsCellItem
              icon={<Edit />}
              onClick={() => editTenantClicked(params.row)}
              label="Edit"
              showInMenu
              disabled={user.proxyRole === "REC"}
            />,
          );
        }

        arr.push(
          <GridActionsCellItem
            icon={<Visibility />}
            onClick={() => handleViewClick(params.row)}
            label="View"
            showInMenu
            disabled={user.proxyRole === "REC"}
          />,
        );

        if (
          !hideOwnerEdit &&
          params.row.type === "Company" &&
          !isEmptyObject(params.row.contact)
        ) {
          arr.push(
            <GridActionsCellItem
              icon={<Contacts />}
              onClick={() => handleEditCompanyContactClicked(params.row)}
              label="Edit Company Contact"
              showInMenu
            />,
          );
        }

        return arr;
      },
    },
  ];

  function handleExportTenantsClicked(tenantType) {
    exportTenants(tenantType);
  }

  function handleExportAccountHoldersClicked() {
    exportAccountHolders();
  }

  return (
    <Permit
      roles="BLDIR,BLDMG,CS,REC,OWN"
      noPermittedServiceMessage={{
        title: "Access Denied",
        body: (
          <>
            Your current role is not permitted to view this page. <br /> Please
            contact your system admin if you feel this is an error.
          </>
        ),
      }}
    >
      <MainContainer>
        <TopBarContainer
          container
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: { lg: "nowrap", md: "wrap" },
            justifyContent: "space-between",
            width: { xs: "100%", md: "80%" },
            backgroundColor: "white",
            padding: { md: "1vh", xs: "1vh" },
            borderRadius: "1vh",
            margin: "20px auto",
            boxShadow: "0 4px 5px 0 rgba(0,0,0,0.5)",
          }}
        >
          <ResponsiveRow
            sx={{
              justifyContent: { md: "space-between", xs: "center" },
              width: { xs: "100%", lg: "50%" },
              padding: 0,
            }}
            component="form"
          >
            <Permit roles="BLDIR,BLDMG,REC,CS,ACC">
              <FormControl
                sx={{
                  margin: { md: "auto 20px", xs: "10px auto " },
                  width: "100%",
                }}
              >
                <InputLabel id="vendorsLabel">
                  {residentialTenant || hoaTenant
                    ? "Search tenant's name"
                    : "Search name"}
                </InputLabel>
                <OutlinedInput
                  id="tenantNameSearch"
                  value={tenantName}
                  label={`Search tenant's name`}
                  sx={{ width: { xs: "100%", lg: "90%" } }}
                  onChange={(event) => {
                    let value = event.target.value;
                    setTenantName(value);
                  }}
                />
              </FormControl>
            </Permit>
          </ResponsiveRow>

          <ResponsiveRow
            sx={{
              justifyContent: { md: "space-between", xs: "center" },
              width: { md: "20%", xs: "100%" },
              padding: 0,
            }}
            component="form"
          >
            <TopBarContainedBrandButton
              sx={{
                width: "100%",
                height: "50px",
              }}
              onClick={
                residentialTenant
                  ? () => handleExportTenantsClicked("residential")
                  : hoaTenant
                    ? () => handleExportTenantsClicked("hoa")
                    : () => handleExportAccountHoldersClicked()
              }
            >
              Export{" "}
              {residentialTenant || hoaTenant ? "Tenants" : "Account Holders"}
            </TopBarContainedBrandButton>
          </ResponsiveRow>
        </TopBarContainer>

        <MuiDataGrid
          noSelection={true}
          dataGridColumns={
            residentialTenant ? columns1 : hoaTenant ? columns3 : columns2
          }
          dataGridRows={tenantList}
          serverPagination={true}
          handlePageLoad={setPaginationModel}
          loading={loading}
          height="60vh"
          totalRows={total}
        />

        {task === "Edit" && selectedTenant.type === "Company" && addOrganizationModal && (
          <AddNewOrganization
            openModal={addOrganizationModal}
            closeModal={handleAddOrgClose}
            allowEditOrg={true}
            task={task}
            selectedOrg={selectedTenant}
          />
        )}

        {task === "Edit" && selectedTenant.type === "Individual" && (
          <AddNewUser
            openModal={addUserModal}
            closeModal={handleAddUserClose}
            saveUser={handleUpdateTenant}
            manageUsers={false}
            allowEditUser={true}
            task={task}
            selectedPerson={selectedTenant}
          />
        )}

        {viewTenantModal && (
          <ViewTenantModal
            openModal={viewTenantModal}
            closeModal={handleViewTenantClose}
            tenantToView={selectedTenant}
          />
        )}

        // edit company contact modal
        <EditUserModal
          showAddNewUserForm={editCompanyContactModal}
          handleAddUserClose={() => {
            setEditCompanyContactModal(false);
            setSelectedCompanyContact();
            setTask("");
          }}
          selectedUser={selectedCompanyContact}
          setSelectedUser={(userUpdate) =>
            setSelectedCompanyContact(userUpdate)
          }
          handleSaveUserClicked={handleSaveCompanyContactClicked}
          idTypes={idTypes}
          userRoles={userRoles}
          isCompanyContact={true}
          task={task}
        />
      </MainContainer>
    </Permit>
  );
}
